import { Box, LinearProgress, Skeleton, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import type { JSX } from "react";

import { salesApi } from "../client.js";

const pct = (n: number | null): string => (n == null ? "—" : `${Math.round(n * 100)}%`);

function Bar({ label, value, color }: { label: string; value: number | null; color: "success" | "primary" }): JSX.Element {
  return (
    <Box sx={{ display: "grid", gap: 0.4, minWidth: 0 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
        <span>{label}</span>
        <span>{pct(value)}</span>
      </Box>
      <LinearProgress variant="determinate" value={value == null ? 0 : value * 100} color={color} sx={{ height: 8, borderRadius: 999 }} />
    </Box>
  );
}

function Pill({ n, label, hue }: { n: number; label: string; hue: string }): JSX.Element {
  return (
    <Box sx={{ display: "inline-flex", alignItems: "baseline", gap: 0.5 }}>
      <Box component="span" aria-hidden sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: hue }} />
      <Typography component="span" sx={{ fontSize: 13, fontWeight: 700, color: "text.primary" }}>{n}</Typography>
      <Typography component="span" sx={{ fontSize: 11, color: "text.secondary" }}>{label}</Typography>
    </Box>
  );
}

/**
 * Lead-quality at a glance — the LLM-judge's average GROUNDING + FIT and the
 * keep/review/reject split (GET /api/sales/v2/quality). Turns "it stopped
 * hallucinating" into a number you can watch.
 *
 * SCOPE: these numbers are across your *active pipeline* — every judged lead
 * still in the working list (status NEW) — NOT "today" and NOT all-time. That
 * was confusing next to the today-scoped tiles ("2 leads today" yet "27
 * judged"), so the strip now states its scope explicitly. Non-critical: a
 * skeleton while loading, hidden on error or when nothing has been judged.
 */
export function QualityStrip(): JSX.Element | null {
  const q = useQuery({
    queryKey: ["sales", "quality"],
    queryFn: salesApi.getQuality,
    refetchInterval: 60_000,
  });

  if (q.isLoading) return <Skeleton variant="rounded" sx={{ height: 110, borderRadius: 1 }} />;
  if (q.isError || !q.data || q.data.evaluated === 0) return null;

  const d = q.data;
  const n = d.evaluated;
  return (
    <Box
      component="section"
      aria-label="Lead quality across the active pipeline"
      sx={{
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: (t: Theme) => t.shadows[1],
        px: 2,
        py: 1.5,
        display: "grid",
        gap: 1.25,
      }}
    >
      {/* Explicit scope — this is the WHOLE open pipeline, not today. */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 0.5 }}>
        <Box component="span" sx={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "text.disabled" }}>
          Lead quality
        </Box>
        <Box component="span" sx={{ fontSize: 10.5, color: "text.disabled" }}>
          {`across all ${n} active ${n === 1 ? "lead" : "leads"} · not just today`}
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: { xs: 1.5, sm: 3 },
          gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) minmax(0, 1fr) auto" },
          alignItems: "center",
        }}
      >
        <Bar label="Avg grounding" value={d.avg_grounding} color="success" />
        <Bar label="Avg fit" value={d.avg_fit} color="primary" />
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
          <Pill n={d.verdicts.keep} label="keep" hue="success.main" />
          <Pill n={d.verdicts.review} label="worth a look" hue="warning.main" />
          <Pill n={d.verdicts.reject} label="reject" hue="error.main" />
        </Box>
      </Box>
    </Box>
  );
}
